import { useEffect } from 'react';

function getTopModalOverlay(): HTMLElement | null {
    const overlays = document.querySelectorAll<HTMLElement>('.modal-overlay');
    if (overlays.length === 0) {
        return null;
    }
    return overlays[overlays.length - 1];
}

function isButtonEnabled(button: HTMLButtonElement | null): button is HTMLButtonElement {
    return !!button && !button.disabled;
}

function findPrimaryFooterButton(footer: Element): HTMLButtonElement | null {
    const candidates: Array<HTMLButtonElement | null> = [
        footer.querySelector('button[type="submit"]'),
        footer.querySelector('button.btn-primary'),
        footer.querySelector('button.btn-active'),
        footer.querySelector('button.btn-delete'),
    ];
    for (const candidate of candidates) {
        if (isButtonEnabled(candidate)) {
            return candidate;
        }
    }
    return null;
}

function findCancelFooterButton(footer: Element): HTMLButtonElement | null {
    const cancel = footer.querySelector<HTMLButtonElement>('button.btn-secondary');
    return isButtonEnabled(cancel) ? cancel : null;
}

/**
 * Enter activates OK/Submit (and confirm Delete) in the topmost modal footer.
 * Escape activates Cancel, or the sole OK button when there is no Cancel.
 * Runs in capture phase so PdfViewer menu shortcuts do not steal the keys.
 */
export function useModalKeyboardShortcuts(): void {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Enter' && e.key !== 'Escape') {
                return;
            }
            if (e.defaultPrevented || e.isComposing) {
                return;
            }
            if (e.ctrlKey || e.metaKey || e.altKey) {
                return;
            }

            const overlay = getTopModalOverlay();
            if (!overlay) {
                return;
            }

            const footer = overlay.querySelector('.modal-footer');
            if (!footer) {
                return;
            }

            if (e.key === 'Escape') {
                const cancel = findCancelFooterButton(footer);
                const target = cancel ?? findPrimaryFooterButton(footer);
                if (!target) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                target.click();
                return;
            }

            // Enter: do not hijack textarea / select / already-focused buttons
            if (e.target instanceof HTMLTextAreaElement) {
                return;
            }
            if (e.target instanceof HTMLSelectElement) {
                return;
            }
            if (e.target instanceof HTMLElement && e.target.isContentEditable) {
                return;
            }
            if (e.target instanceof HTMLButtonElement && overlay.contains(e.target)) {
                return;
            }

            const primary = findPrimaryFooterButton(footer);
            if (!primary) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            primary.click();
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, []);
}
